import { Injectable } from '@angular/core';
import {
  ArchObjHierarchyViewModel,
  ArchObjToAddressViewModel,
  LocationClient,
} from '@core/api';
import { FormlyFieldBaseService } from '@core/services/formly-service/formly-field.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import {
  FormlyPreviewAutocompleteColumnDefinition,
  FormlyPreviewAutocompleteProps,
} from './formly-service.types';

@Injectable({
  providedIn: 'root',
})
export class LocationFormlyFieldService extends FormlyFieldBaseService {
  override translatePath = 'location.autocomplete';
  public defaultList$ = new BehaviorSubject<any[]>([]);

  private readonly resetSelection$: Subject<void> = new Subject<void>();

  private searchOptionWithInactive$ = new BehaviorSubject<boolean>(false);

  constructor(
    protected override translateService: TranslateService,
    private locationClient: LocationClient
  ) {
    super(translateService);
  }

  resetSelection() {
    this.resetSelection$.next();
  }

  setSearchOption(option: boolean) {
    this.searchOptionWithInactive$.next(option);
  }

  autocompleteSearch(
    searchBuildings: boolean,
    defaultValue: ArchObjHierarchyViewModel,
    showDefaultList: boolean,
    onSelect?: (id: string, onInitSearch: boolean) => void,
    columnDefinition?: (
      | {
          fieldName: string;
          labelTranslationKey: string;
          classes: string;
          formatter?: undefined;
          maxWidth?: number;
        }
      | {
          fieldName: string;
          labelTranslationKey: string;
          classes: string;
          formatter: (x: any) => any;
          maxWidth?: number;
        }
    )[],
    onDelete?: () => void,
    tooltip?: string,
    key?: string,
    required?: boolean,
    label?: string
  ): FormlyFieldConfig[] {
    const translatePath = 'location.autocomplete';

    if (showDefaultList) {
      this.getDefaultList();
    } else {
      this.defaultList$.next([]);
    }

    let columnDefinitions: FormlyPreviewAutocompleteColumnDefinition[] = [
      {
        fieldName: 'id',
        labelTranslationKey: translatePath + '.WE-No',
        classes: 'text-overflow',
        maxWidth: 100,
      },
      {
        fieldName: 'postalCode',
        labelTranslationKey: translatePath + '.zip',
        classes: '',
        formatter: (x: ArchObjToAddressViewModel) =>
          x?.address?.postalCode ?? '',
        maxWidth: 70,
      },
      {
        fieldName: 'city',
        labelTranslationKey: translatePath + '.city',
        classes: 'text-overflow',
        formatter: (x: ArchObjToAddressViewModel) => x?.address?.city ?? '',
        maxWidth: 170,
      },
      {
        fieldName: 'streetName',
        labelTranslationKey: translatePath + '.street',
        classes: 'text-overflow',
        formatter: (x: ArchObjToAddressViewModel) =>
          x?.address?.streetName ??
          '' + (x?.address?.houseNumber ? ' ' + x?.address?.houseNumber : ''),
      },
      {
        fieldName: 'houseNumber',
        labelTranslationKey: translatePath + '.housenumber',
        classes: 'text-overflow',
        formatter: (x: ArchObjToAddressViewModel) =>
          x?.address?.houseNumber ?? '',
        maxWidth: 120,
      },
      {
        fieldName: 'favorite',
        labelTranslationKey: '',
        classes: 'icon',
        type: 'icon',
        icon: 'star',
        formatter: (x: ArchObjHierarchyViewModel) => {
          if (!x?.isFavorite) {
            return '';
          }
          return 'star';
        },
        hideInPreview: true,
        maxWidth: 60,
      },
    ];

    if (columnDefinition) {
      columnDefinitions = columnDefinition;
    }

    if (searchBuildings) {
      columnDefinitions.splice(1, 0, {
        fieldName: 'siteAdditionalNumber',
        labelTranslationKey: translatePath + '.WE-Additional-No',
        classes: '',
      });
    }

    const props: FormlyPreviewAutocompleteProps = {
      required: required ?? true,
      visibleInConfirm: true,
      bindLabel: 'id',
      bindValue: 'id',
      columnDefinitions,
      defaultValue,
      defaultList: this.defaultList$,
      onSearch: (searchText: string): Observable<any> => {
        return this.locationClient
          .searchArchobjForAutoCompleteInexact(
            searchText,
            this.searchOptionWithInactive$.value
          )
          .pipe(map(x => x.entites));
      },
      onSelect,
      onDelete,
      label:
        label ??
        this.translateService.instant('location.autocomplete.label__required'),
      placeholder: this.translateService.instant(
        'location.autocomplete.label__placeholder'
      ),
      ariaLabel: this.translateService.instant(
        'location.autocomplete.label__ariaLabel'
      ),
      resetSelection$: this.resetSelection$,
      tooltip: tooltip,
      getChipLabel: (option: ArchObjHierarchyViewModel) =>
        `WE: ${option?.id.substring(0, 5)}`,
    };

    return [
      {
        type: 'preview-autocomplete',
        key: key ?? 'searchResult',
        props: { ...props },
        validation: {
          messages: {
            required: this.translateService.instant(
              'formErrors.location.empty'
            ),
          },
        },
      },
    ];
  }

  public getDefaultList() {
    this.locationClient?.getFavorites().subscribe({
      next: res => {
        this.defaultList$.next(res);
      },
    });
  }
}
